import { useState } from 'react'
import { Alert, Button, Empty, Flex, Typography, theme } from 'antd'
import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { displayName } from '@/shared/lib/planName'
import { addMoney, formatMoneyValue, negateMoney, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { CashFeedbackAlert } from './CashFeedbackAlert'
import { ConfirmAction } from './ConfirmAction'
import { fundSubscriber, issueGrant, type DirectoryRow } from './cashdeskApi'
import type { CashPlan } from './cashdeskPlans'
import type { CashFeedback } from './useCashOperation'
import { useCombinedSale, type SaleStep } from './useCombinedSale'

interface GrantPanelProps {
  subscriber: DirectoryRow
  /** ⚠️ FAQAT sotiladiganlari — chaqiruvchi filtrlaydi */
  plans: readonly CashPlan[]
  /** Kassirning tuguni; `null` — to'ldirish qadami umuman mumkin emas */
  agentId: string | null
  /** Kassir hozir sarflay oladigan summa (depozit + qarz limiti) */
  available: Money | null
  /** Bitta to'lovning shifti; `null` — chegara yo'q yoki noma'lum */
  perTxnLimit: Money | null
  /** ⚠️ `false` — reseller: to'ldirish qadami CHIZILMAYDI (ADR-029) */
  canFund: boolean
  onBack: () => void
}

/** Sotuv boshlangandagi QOTIRILGAN qaror — qayta urinish ham shundan yuradi */
interface Commitment {
  plan: CashPlan
  /** `null` — to'ldirish qadami yo'q, faqat paket */
  fund: Money | null
}

/**
 * Paket narxi bilan abonent balansi orasidagi FARQ; yetarli bo'lsa `null`.
 *
 * ⚠️ Balans NOMA'LUM bo'lsa (`null` — ledger javob bermagan) ham `null`
 * qaytadi: bilinmagan sondan farq hisoblab bo'lmaydi va nolga tenglashtirish
 * «yetarli» degan yolg'onga aylanardi.
 *
 * ⚠️ Valyutalar mos kelmasa ham `null` — konvertatsiya panelda YO'Q
 * (`money.ts` dagi `addMoney` bilan bir xil qoida).
 */
function shortfallOf(plan: CashPlan, balance: Money | null): Money | null {
  if (balance === null || balance.currency !== plan.price.currency) return null
  if (balance.amount >= plan.price.amount) return null
  return { amount: plan.price.amount - balance.amount, currency: plan.price.currency }
}

/**
 * Paket berish — va kerak bo'lsa BIR TASDIQ ostida balansni to'ldirish.
 *
 * <h2>⚠️ NEGA IKKI AMAL BITTA EKRANDA (egasi qarori, §10-3)</h2>
 * Abonentda puli bo'lmasa kassir ilgari ikki ekran orasida yugurardi:
 * balansni to'ldiradi, orqaga qaytadi, paketni qaytadan tanlaydi.
 * Abonent esa qarshisida turibdi. Endi ikkalasi bitta tasdiq ostida:
 * tugmada IKKALA summa ham yozilgan.
 *
 * <h2>⚠️ LEKIN BU BITTA AMAL EMAS — IKKITA</h2>
 * Pul ikki xil hisobdan ko'chadi (kassir depoziti → abonent balansi →
 * paket) va ular ALOHIDA ledger yozuvi, alohida idempotentlik kaliti.
 * Ya'ni «pul o'tdi, paket berilmadi» — haqiqiy holat va u YASHIRILMAYDI:
 * ekranda ikkita alohida natija turadi, qayta urinish esa FAQAT qolgan
 * qadamni yuboradi. Butun oqimni qaytadan yurgizish abonentga ikki
 * barobar pul o'tkazardi (`useCombinedSale` izohi).
 *
 * <h2>⚠️ To'ldirish qadami O'ZIDAN paydo bo'ladi</h2>
 * Uchinchi tugma qo'shilmadi: blok faqat tanlangan paket narxi
 * abonentning balansidan katta bo'lganda chiziladi. Balansi yetarli
 * abonentda ekran avvalgidek — bitta ro'yxat va bitta tugma.
 */
export function GrantPanel({
  subscriber,
  plans,
  agentId,
  available,
  perTxnLimit,
  canFund,
  onBack,
}: GrantPanelProps) {
  const { t, i18n } = useTranslation()
  const { token } = theme.useToken()
  const [planId, setPlanId] = useState<string | null>(null)
  const [topUp, setTopUp] = useState<Money | null>(null)
  const [armed, setArmed] = useState(false)
  const [commitment, setCommitment] = useState<Commitment | null>(null)
  const sale = useCombinedSale()

  const selected = plans.find((p) => p.id === planId) ?? null
  const balance: Money | null = subscriber.balance
  const canTopUp = canFund && agentId !== null
  const shortfall = selected === null ? null : shortfallOf(selected, balance)
  const needsTopUp = canTopUp && shortfall !== null

  /**
   * ⚠️ TUGAMAGAN SOTUV — «pul ko'chgan, lekin oxirigacha bormagan» holat.
   *
   * Manba `settled`, `commitment` EMAS: mablag' yetmagani uchun rad
   * etilgan urinishda hech narsa ko'chmaydi va kassir summani DARHOL
   * tuzata olishi kerak (§4.4). Faqat haqiqatan pul o'tgan holatda
   * ro'yxat va maydon qulflanadi.
   */
  const unfinished = sale.settled.size > 0 ? commitment : null
  const locked = sale.running || unfinished !== null

  /** Tasdiq tugmasida ko'rsatiladigan to'ldirish summasi */
  const plannedFund = needsTopUp && topUp !== null && topUp.amount > 0 ? topUp : null

  const overDeposit =
    plannedFund !== null &&
    available !== null &&
    available.currency === plannedFund.currency &&
    plannedFund.amount > available.amount

  const remainingDeposit =
    plannedFund !== null && available !== null && available.currency === plannedFund.currency
      ? addMoney(available, negateMoney(plannedFund))
      : null

  // ⚠️ Birlashgan sotuvda TO'LDIRISH bosqichi ham shu shift ostida:
  // birinchi qadam rad etilsa paket ham berilmaydi va kassir «qisman
  // bajarildi» ekranida sababsiz qolardi.
  const overPerTxn =
    plannedFund !== null &&
    perTxnLimit !== null &&
    perTxnLimit.currency === plannedFund.currency &&
    plannedFund.amount > perTxnLimit.amount

  function buildSteps(commit: Commitment): SaleStep[] {
    const steps: SaleStep[] = []
    const fund = commit.fund
    const plan = commit.plan

    if (fund !== null && agentId !== null) {
      steps.push({
        id: 'fund',
        signature: `fund:${subscriber.id}:${fund.amount}:${fund.currency}`,
        keyPrefix: 'kassa-fund',
        run: async (key): Promise<CashFeedback> => {
          const result = await fundSubscriber(agentId, subscriber.id, fund, key)
          if (result.status === 'OK') return { tone: 'success', message: result.message }
          if (result.status === 'PARTIAL') return { tone: 'partial', message: result.message }
          return {
            tone: 'refused',
            message: result.message,
            hint: t('cashdesk.fund.depositHint', { amount: formatMoneyValue(available) }),
          }
        },
      })
    }

    steps.push({
      id: 'grant',
      signature: `grant:${subscriber.id}:${plan.id}`,
      keyPrefix: 'kassa-grant',
      run: async (key): Promise<CashFeedback> => {
        const outcome = await issueGrant(subscriber.id, plan.id, key)
        if (outcome.kind === 'ISSUED') {
          return {
            tone: 'success',
            message: outcome.replay
              ? t('cashdesk.grant.replay', { plan: displayName(plan, i18n.language) })
              : t('cashdesk.grant.issued', {
                  plan: displayName(plan, i18n.language),
                  login: subscriber.login,
                }),
          }
        }
        return {
          tone: 'refused',
          message: outcome.serverText ?? t('cashdesk.grant.noFunds'),
          // ⚠️ To'ldirish qadami bo'lgan oqimda ekrandagi balans
          // ESKIRGAN bo'lishi mumkin — noto'g'ri sonni ishora qilib
          // ko'rsatishdan ko'ra umuman ko'rsatmaslik halolroq.
          ...(fund === null
            ? { hint: t('cashdesk.grant.balanceHint', { amount: formatMoneyValue(balance) }) }
            : {}),
        }
      },
    })

    return steps
  }

  const finish = (allMoved: boolean) => {
    setArmed(false)
    if (!allMoved) return
    setPlanId(null)
    setTopUp(null)
    setCommitment(null)
    // ⚠️ Kalitlar yangilanadi: usiz shu abonentga o'sha paketni
    // IKKINCHI marta berish server tomonidan «takror» deb rad etilardi.
    sale.startOver()
  }

  const start = () => {
    if (selected === null) return
    const commit: Commitment = { plan: selected, fund: plannedFund }
    setCommitment(commit)
    void sale.run(buildSteps(commit)).then(finish)
  }

  const retryRemaining = () => {
    if (unfinished === null) return
    // ⚠️ QOLGAN QADAM — bu yerda ham ochiq filtrlanadi. `useCombinedSale`
    // o'tgan qadamni baribir tashlab ketardi, lekin niyat KODDA ko'rinib
    // tursin: qayta urinish hech qachon pulni ikkinchi marta ko'chirmaydi.
    const rest = buildSteps(unfinished).filter((step) => !sale.settled.has(step.id))
    void sale.run(rest).then(finish)
  }

  /**
   * Tugamagan sotuvni YOPIB, noldan boshlash.
   *
   * ⚠️ Bu chiqish yo'li ZARUR, chunki qayta urinish har doim ham yordam
   * bermaydi: to'ldirish o'tib, paket «mablag' yetmadi» bilan rad
   * etilgan bo'lsa (kassir taklif qilingan summani kamaytirgan), o'sha
   * qadamni takrorlash aynan shunday rad etilardi. Kerak bo'lgani —
   * yana pul qo'yish, ya'ni YANGI amal.
   *
   * ⚠️ Kalitlar ham tozalanadi va bu xavfsiz: `settled` da faqat
   * SERVER TASDIQLAGAN qadamlar turadi. Ya'ni bu tugma «noma'lum
   * natijani unutish» emas, «tugagan amaldan keyin yangisini boshlash».
   */
  const startFresh = () => {
    sale.reset()
    setCommitment(null)
    setArmed(false)
  }

  const confirmLabel =
    selected === null
      ? ''
      : plannedFund !== null
        ? t('cashdesk.sale.confirm', {
            fund: formatMoneyValue(plannedFund),
            grant: formatMoneyValue(selected.price),
          })
        : t('cashdesk.grant.confirm', { amount: formatMoneyValue(selected.price) })

  return (
    <Flex vertical gap={12}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('common.back')}
      </Button>

      {/* Har qadamning natijasi ALOHIDA va nomi bilan — qisman
          muvaffaqiyatda aynan shu ikki qator qaysi pul ko'chganini
          aytadi. */}
      <CashFeedbackAlert
        feedback={sale.outcomes.fund ?? null}
        stepLabel={t('cashdesk.sale.fundStep')}
      />
      <CashFeedbackAlert
        feedback={sale.outcomes.grant ?? null}
        stepLabel={t('cashdesk.sale.grantStep')}
      />

      {unfinished !== null ? (
        <Alert
          type="warning"
          showIcon
          message={t('cashdesk.sale.partialTitle')}
          description={
            <Flex vertical gap={8} align="flex-start">
              <span>{t('cashdesk.sale.partialText')}</span>
              <Flex gap={8} wrap>
                <Button
                  type="primary"
                  loading={sale.running}
                  disabled={sale.running}
                  onClick={retryRemaining}
                >
                  {t('cashdesk.sale.retryGrant')}
                </Button>
                <Button type="text" disabled={sale.running} onClick={startFresh}>
                  {t('cashdesk.sale.startFresh')}
                </Button>
              </Flex>
            </Flex>
          }
        />
      ) : null}

      {plans.length === 0 ? (
        <Empty description={t('cashdesk.grant.noPlans')} />
      ) : (
        <Flex vertical gap={8}>
          {plans.map((plan) => {
            const active = plan.id === planId
            return (
              <div
                key={plan.id}
                role="button"
                aria-disabled={locked}
                tabIndex={locked ? -1 : 0}
                onClick={() => {
                  if (locked) return
                  setPlanId(plan.id)
                  // Taklif: aynan yetishmagan summa. Kassir uni
                  // o'zgartira oladi — qaror baribir serverniki.
                  setTopUp(canTopUp ? shortfallOf(plan, balance) : null)
                }}
                onKeyDown={(e) => {
                  if (locked) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    setPlanId(plan.id)
                    setTopUp(canTopUp ? shortfallOf(plan, balance) : null)
                  }
                }}
                style={{
                  minHeight: 56,
                  padding: '10px 14px',
                  borderRadius: token.borderRadiusLG,
                  border: `1px solid ${active ? token.colorPrimary : token.colorBorderSecondary}`,
                  background: active ? token.colorPrimaryBg : token.colorBgContainer,
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked && !active ? 0.5 : 1,
                }}
              >
                <Flex align="center" justify="space-between" gap={12}>
                  <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                    {active ? <CheckOutlined style={{ color: token.colorPrimary }} /> : null}
                    <Typography.Text ellipsis style={{ fontSize: 15 }}>
                      {displayName(plan, i18n.language)}
                    </Typography.Text>
                  </Flex>
                  <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
                    {formatMoneyValue(plan.price)}
                  </Typography.Text>
                </Flex>
              </div>
            )
          })}
        </Flex>
      )}

      {/* ⚠️ TO'LDIRISH QADAMI — faqat kerak bo'lganda va faqat naqd
          oladigan sherikda. Reseller uchun blok umuman chizilmaydi:
          uning uchun bu tugma har doim 403 bo'lardi (ADR-029). */}
      {selected !== null && needsTopUp && shortfall !== null ? (
        <Flex vertical gap={8}>
          <Alert
            type="info"
            showIcon
            message={t('cashdesk.sale.needTopUp', { amount: formatMoneyValue(shortfall) })}
          />
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t('cashdesk.sale.topUpLabel')}
            </Typography.Text>
            <MoneyInput
              value={topUp}
              onChange={setTopUp}
              currency={selected.price.currency}
              size="large"
              disabled={locked}
            />
          </div>
        </Flex>
      ) : null}

      {/* Balansi yetmayapti, lekin to'ldirish bu sherikka ochiq emas */}
      {selected !== null && !canTopUp && shortfallOf(selected, balance) !== null ? (
        <Typography.Text type="warning" style={{ fontSize: 13 }}>
          {t('cashdesk.grant.shortBalance', { amount: formatMoneyValue(balance) })}
        </Typography.Text>
      ) : null}

      {/*
        ⚠️ TASDIQ TUGMASI YOPISHQOQ — ro'yxat uzunligidan QAT'I NAZAR.

        Egasi talabi (2026-08-21): paket tanlangandan keyin tugmaga
        «pastga tushish» kerak bo'lmasin. O'lchangan sabab: `plans`
        cheklovsiz chiziladi va har qatori 56px + 8px, ya'ni 15 paketli
        katalogda tugma ekrandan ~960px pastda qolardi.

        ⚠️ `sticky`, `fixed` EMAS — bu o'lchov natijasi, did emas.
        Telefonda ich `Drawer` ichida va aylanish `.ant-drawer-body` da
        (antd unga `overflow: auto` beradi); `lg` dan keng oynada esa
        hujjatning o'zi aylanadi. `sticky` ikkala holatda ham O'Z
        aylanuvchisiga yopishadi va `Drawer` chegarasidan CHIQMAYDI,
        ya'ni pastki tab-bar (`MobileTabBar`, 64px + safe-area) bosib
        qolinmaydi. `fixed` bo'lganda tugma ekranga yopishib, aynan
        o'sha tab-bar ustiga tushardi.

        ⚠️ Ro'yxatga `maxHeight` + o'z aylanishi RAD ETILDI: aylanuvchi
        ichidagi ikkinchi aylanuvchi barmoq ostida qaysi biri
        surilayotganini noaniq qiladi, balandlik esa o'ylab topilgan son
        bo'lardi. Tanlangan paketni tepaga ko'chirish ham rad etildi —
        ro'yxat barmoq ostida qayta tuziladi va keyingi tegish boshqa
        paketga tushardi.

        ⚠️ Tanlanmagan holatda tugma O'CHIQ turadi, YASHIRILMAYDI. Ikki
        sabab: yashirish `ConfirmAction` ni qayta mount qilardi va
        idempotentlik kaliti yangilanardi (`ConfirmAction` javadoc'i);
        ikkinchidan, chiqib-yo'qoladigan panel ro'yxatni tanlash paytida
        siljitardi.

        ⚠️ Shift va depozit ogohlantirishlari SHU YERGA ko'chdi: ular
        tugmani o'chiradigan yoki tugmadagi summani izohlaydigan yagona
        matn, ro'yxat ostida qolsa kassir «o'chiq tugma, sababi yo'q»
        ekranida qolardi.
      */}
      <Flex
        vertical
        gap={8}
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          paddingTop: 12,
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          // `colorBgElevated`: `Drawer` ning sirti aynan shu token, ya'ni
          // telefonda chok ko'rinmaydi; kartochka ichida esa u bir pog'ona
          // yorug'roq bo'lib, chegara bilan birga «amal qatori» bo'lib o'qiladi.
          background: token.colorBgElevated,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        {overDeposit ? (
          <Typography.Text type="warning" style={{ fontSize: 13 }}>
            {t('cashdesk.fund.overDeposit', { amount: formatMoneyValue(available) })}
          </Typography.Text>
        ) : null}
        {overPerTxn ? (
          <Typography.Text type="danger" style={{ fontSize: 13 }}>
            {t('cashdesk.fund.overPerTxn', { amount: formatMoneyValue(perTxnLimit) })}
          </Typography.Text>
        ) : null}
        <ConfirmAction
          label={t('cashdesk.grant.action')}
          confirmLabel={confirmLabel}
          disabled={selected === null || locked || overPerTxn}
          running={sale.running}
          armed={armed}
          onArm={() => setArmed(true)}
          onCancel={() => setArmed(false)}
          // ⚠️ ConfirmAction NING KALITI BU YERDA ISHLATILMAYDI. U mount'ga
          // bog'langan bitta kalit beradi, bu oqimda esa IKKI kalit kerak va
          // ularning umri tasdiq bosqichidan uzun: qisman muvaffaqiyatdan
          // keyin tasdiq yopiladi, qayta urinish esa AYNAN O'SHA kalit bilan
          // ketishi shart — aks holda birinchi urinish serverga yetib
          // borgan bo'lsa, ikkinchisi ikkinchi paketni berardi.
          onConfirm={() => start()}
          keyPrefix="kassa-sale"
          seed={`${subscriber.id}:${planId ?? ''}:${plannedFund?.amount ?? 0}`}
          summary={
            <>
              <Typography.Text strong style={{ fontSize: 15, display: 'block' }}>
                {t('cashdesk.grant.summary', {
                  plan: selected ? displayName(selected, i18n.language) : t('common.dash'),
                  login: subscriber.login,
                })}
              </Typography.Text>
              {plannedFund !== null ? (
                <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                  {t('cashdesk.sale.summaryFund', { amount: formatMoneyValue(plannedFund) })}
                </Typography.Text>
              ) : null}
              <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                {t('cashdesk.grant.chargeNote', {
                  amount: selected ? formatMoneyValue(selected.price) : t('common.dash'),
                })}
              </Typography.Text>
              {remainingDeposit !== null ? (
                <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                  {t('cashdesk.fund.afterDeposit', { amount: formatMoneyValue(remainingDeposit) })}
                </Typography.Text>
              ) : null}
            </>
          }
        />
      </Flex>
    </Flex>
  )
}
